import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building, 
  Users, 
  TrendingUp, 
  Server,
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  Shield,
  Database,
  Euro,
  MessageSquare
} from 'lucide-react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { adminApi } from '../../services/api';
import { AdminHomeSummary } from '../../types';
import { useImpersonation } from '../../context/ImpersonationContext';

interface QuickStatProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  trend?: { value: number; label: string };
  color: 'primary' | 'success' | 'warning' | 'error';
}

const QuickStat: React.FC<QuickStatProps> = ({ title, value, icon, trend, color }) => {
  const colorClasses = {
    primary: 'text-primary bg-primary/10',
    success: 'text-green-500 bg-green-500/10',
    warning: 'text-yellow-500 bg-yellow-500/10',
    error: 'text-red-500 bg-red-500/10',
  };

  return (
    <div className="dark:bg-surface bg-light-surface rounded-xl p-6 border dark:border-border-color border-light-border-color">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm dark:text-text-secondary text-light-text-secondary">{title}</p>
          <p className="text-3xl font-bold mt-1 dark:text-white text-gray-900">{value}</p>
          {trend && (
            <p className={`text-sm mt-1 ${trend.value >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {trend.value >= 0 ? '+' : ''}{trend.value}% {trend.label}
            </p>
          )}
        </div>
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          {icon}
        </div>
      </div>
    </div>
  );
};

const SuperAdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { startImpersonation } = useImpersonation();
  const [homeSummary, setHomeSummary] = useState<AdminHomeSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const summary = await adminApi.getAdminHomeSummary();
      setHomeSummary(summary);
      setError(null);
    } catch (err) {
      setError('Failed to load admin dashboard data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleManageTenant = (tenant: AdminHomeSummary['recentTenants'][number]) => {
    startImpersonation(tenant.tenantId, tenant.tenantName);
    navigate('/dashboard');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-primary/10 rounded-lg">
          <Shield className="w-8 h-8 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold dark:text-white text-gray-900">
            Super Admin Dashboard
          </h1>
          <p className="dark:text-text-secondary text-light-text-secondary">
            System-wide overview and tenant management
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500" />
          <p className="text-red-500">{error}</p>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <QuickStat
          title="Active Tenants"
          value={homeSummary?.metrics.activeTenants ?? '-'}
          icon={<CheckCircle className="w-6 h-6" />}
          color="success"
        />
        <QuickStat
          title="Total Users"
          value={homeSummary?.metrics.totalUsers ?? '-'}
          icon={<Users className="w-6 h-6" />}
          color="primary"
        />
        <QuickStat
          title="Total Revenue"
          value={formatCurrency(homeSummary?.metrics.totalRevenue || 0)}
          icon={<Euro className="w-6 h-6" />}
          color="primary"
        />
        <QuickStat
          title="Conversations"
          value={
            homeSummary?.metrics.totalConversations !== undefined
              ? homeSummary.metrics.totalConversations.toLocaleString()
              : 0
          }
          icon={<MessageSquare className="w-6 h-6" />}
          color="warning"
        />
      </div>

      {/* Recent Tenants */}
      <div className="grid grid-cols-1 gap-6">
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold dark:text-white text-gray-900">
                Tenants
              </h2>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate('/admin/tenants')}
              >
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            
            <div className="space-y-3">
              {homeSummary?.recentTenants?.map((tenant) => (
                <div
                  key={tenant.tenantId}
                  className="flex items-center justify-between p-4 rounded-lg dark:bg-background bg-gray-50 border dark:border-border-color border-light-border-color hover:border-primary/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Building className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium dark:text-white text-gray-900">
                        {tenant.tenantName}
                      </p>
                      <p className="text-sm dark:text-text-secondary text-light-text-secondary">
                        {tenant.tenantId} • {tenant.userCount || 0} users
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      tenant.status === 'active' 
                        ? 'bg-green-500/10 text-green-500' 
                        : tenant.status === 'suspended'
                        ? 'bg-red-500/10 text-red-500'
                        : 'bg-yellow-500/10 text-yellow-500'
                    }`}>
                      {tenant.status}
                    </span>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleManageTenant(tenant)}
                    >
                      Manage
                    </Button>
                  </div>
                </div>
              ))}
              {(!homeSummary?.recentTenants || homeSummary.recentTenants.length === 0) && (
                <div className="text-center py-8 dark:text-text-secondary text-light-text-secondary">
                  No tenants found
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>

      {/* System Health (placeholder) */}
      <Card>
        <div className="p-6">
          <h2 className="text-lg font-semibold dark:text-white text-gray-900 mb-4">
            System Health
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { label: 'API Server', icon: <Server className="w-5 h-5" />, data: homeSummary?.health.apiServer },
              { label: 'Database', icon: <Database className="w-5 h-5" />, data: homeSummary?.health.database },
              { label: 'RAG Service', icon: <TrendingUp className="w-5 h-5" />, data: homeSummary?.health.ragService },
            ].map(({ label, icon, data }) => {
              const status = data?.status || 'unreachable';
              const healthy = data?.healthy;
              const color =
                status === 'operational'
                  ? 'text-green-500'
                  : status === 'degraded'
                  ? 'text-yellow-500'
                  : 'text-red-500';
              const bg =
                status === 'operational'
                  ? 'bg-green-500/10'
                  : status === 'degraded'
                  ? 'bg-yellow-500/10'
                  : 'bg-red-500/10';
              return (
                <div key={label} className="flex items-center gap-3 p-4 rounded-lg dark:bg-background bg-gray-50">
                  <div className={`p-2 rounded-full ${bg}`}>
                    {React.cloneElement(icon as React.ReactElement, { className: `w-5 h-5 ${color}` })}
                  </div>
                  <div>
                    <p className="font-medium dark:text-white text-gray-900">{label}</p>
                    <p className={`text-sm ${color}`}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                      {!healthy && data?.error ? ` • ${data.error}` : ''}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default SuperAdminDashboard;
